import {EntityIdentifier} from "@ots-frontend/ots-data";
import {useForm} from "@mantine/form";
import {useTranslation} from "react-i18next";
import {Button, Modal, ModalProps} from "@mantine/core";
import {useCallback} from "react";
import {EntityTypeSelect, SelectableEntityType} from "../../select/entity/EntityTypeSelect/EntityTypeSelect";
import {EntitySelect} from "../../select/entity/EntitySelect/EntitySelect";

interface EntitySelectFormProps {
  options?: EntitySelectFormOptions;
  defaultValues?: Partial<EntitySelectFormValues>;
  onSubmit?: (values: EntitySelectFormValues) => void;
  submitting?: boolean;
}

export interface EntitySelectFormOptions {
  options?: string[] | SelectableEntityType[];
  regionId?: string
}

export interface EntitySelectFormValues {
  object: EntityIdentifier
}

const getDefaultValues = (defaultValues: Partial<EntitySelectFormValues> | undefined) => {
  return {
    object: {
      id: defaultValues?.object?.id,
      type: defaultValues?.object?.type,
    },
  }
}

export const EntitySelectForm = (props: EntitySelectFormProps) => {
  const {
    options,
    defaultValues,
    submitting,
    onSubmit,
  } = props;

  const {t} = useTranslation(["common"]);

  const form = useForm({
    initialValues: getDefaultValues(defaultValues),
    validate: {}
  });

  const onFormSubmit = useCallback(async (values: typeof form.values) => {
    if (onSubmit) {
      onSubmit({
        object: {
          id: values.object.id!,
          type: values.object.type!,
        },
      });
    }
  }, [form, onSubmit]);

  return (
    <form onSubmit={form.onSubmit(onFormSubmit)}>
      <EntityTypeSelect
        options={options?.options}
        label={t("common.entity.select.form.field.object", "Object")}
        placeholder={t<string>("common.entity.select.form.field.object.placeholder", "Object")}
        withinPortal
        withAsterisk
        required
        clearable
        {...form.getInputProps("object.type")} />
      <EntitySelect
        pt="md"
        entityType={form.getTransformedValues().object?.type}
        regionId={options?.regionId}
        withinPortal
        withAsterisk
        required
        clearable
        {...form.getInputProps("object.id")} />
      <Button type="submit" loading={submitting} fullWidth mt="xl">
        {t("common.action.submit", {ns: "common"})}
      </Button>
    </form>
  );
};

interface EntitySelectFormModalProps extends ModalProps {
  form: EntitySelectFormProps
}

export const EntitySelectFormModal = (props: EntitySelectFormModalProps) => {
  const {
    form,
    ...rest
  } = props;

  return (
    <Modal {...rest}>
      <EntitySelectForm{...form} />
    </Modal>
  );
};
